import { useState, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Box, Grid, TextField, Button, MenuItem, FormControlLabel, Switch,
  Typography, Alert, CircularProgress, InputAdornment, alpha, useTheme,
} from '@mui/material';
import { CheckCircle, LocationOn, QrCode2 } from '@mui/icons-material';
import type { FormDefinition, FormFieldDef } from '../../services/platform/runtimeService';
import { submitDynamicForm } from '../../services/platform/runtimeService';
import FormFileUpload from '../ui/forms/FormFileUpload';
import { isAssetUrlField } from '../../utils/assetUrl';
import { getCurrentMapCenter } from '../../utils/mapCenter';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';

interface DynamicFormRendererProps {
  form: FormDefinition;
  onSuccess?: (data: Record<string, unknown>) => void;
  submitLabel?: string;
}

function FieldRenderer({
  field,
  value,
  onChange,
  error,
}: {
  field: FormFieldDef;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
  error?: string;
}) {
  const { t } = useI18n();
  const label = field.label ? translateUiText(field.label, t) : field.label;
  const placeholder = field.placeholder ? translateUiText(field.placeholder, t) : field.placeholder;
  const common = {
    fullWidth: true,
    size: 'small' as const,
    label,
    required: field.required,
    placeholder,
    value: value ?? '',
    error: !!error,
    helperText: error,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(field.key, e.target.value),
  };

  switch (field.type) {
    case 'number':
    case 'currency':
      return <TextField {...common} type="number" inputProps={{ min: field.validation?.min, max: field.validation?.max }} />;
    case 'date':
      return <TextField {...common} type="date" InputLabelProps={{ shrink: true }} />;
    case 'datetime':
      return <TextField {...common} type="datetime-local" InputLabelProps={{ shrink: true }} />;
    case 'email':
      return <TextField {...common} type="email" />;
    case 'phone':
      return <TextField {...common} type="tel" />;
    case 'textarea':
      return <TextField {...common} multiline rows={3} />;
    case 'boolean':
    case 'checkbox':
      return (
        <FormControlLabel
          control={<Switch checked={!!value} onChange={(e) => onChange(field.key, e.target.checked)} />}
          label={label}
        />
      );
    case 'select':
    case 'dropdown':
    case 'radio':
      return (
        <TextField {...common} select>
          {(field.options || []).map((o) => (
            <MenuItem key={o.value} value={o.value}>{translateUiText(o.label, t)}</MenuItem>
          ))}
        </TextField>
      );
    case 'multiselect': {
      const selected = Array.isArray(value)
        ? value.map(String)
        : String(value || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      return (
        <TextField
          {...common}
          select
          value={selected}
          SelectProps={{
            multiple: true,
            renderValue: (selectedVals) => (selectedVals as string[]).join(', '),
          }}
          onChange={(e) => {
            const next = e.target.value;
            onChange(field.key, typeof next === 'string' ? next.split(',') : next);
          }}
        >
          {(field.options || []).map((o) => (
            <MenuItem key={o.value} value={o.value}>{translateUiText(o.label, t)}</MenuItem>
          ))}
        </TextField>
      );
    }
    case 'file':
    case 'camera':
      return (
        <FormFileUpload
          label={label}
          value={String(value ?? '')}
          onChange={(url) => onChange(field.key, url)}
          folder={`forms/${field.key}`}
          accept={field.type === 'camera' ? 'image/*' : undefined}
          required={field.required}
        />
      );
    case 'url':
    case 'image':
      if (isAssetUrlField({ key: field.key, label: field.label, type: field.type === 'image' ? 'image' : 'url' })) {
        return (
          <FormFileUpload
            label={label}
            value={String(value ?? '')}
            onChange={(url) => onChange(field.key, url)}
            folder={`forms/${field.key}`}
            required={field.required}
          />
        );
      }
      return <TextField {...common} type="url" />;
    case 'signature':
      return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2, textAlign: 'center', minHeight: 80 }}>
          <Typography variant="caption" color="text.secondary">Signature pad (metadata field)</Typography>
          <Button size="small" sx={{ mt: 1 }} onClick={() => onChange(field.key, 'signed')}>Sign</Button>
        </Box>
      );
    case 'gps':
      return (
        <Button
          variant="outlined"
          fullWidth
          startIcon={<LocationOn />}
          onClick={async () => {
            const pos = await getCurrentMapCenter();
            onChange(field.key, `${pos.lat},${pos.lng}`);
          }}
        >
          Capture GPS Location
        </Button>
      );
    case 'qr':
    case 'barcode':
      return (
        <TextField {...common} InputProps={{ startAdornment: <InputAdornment position="start"><QrCode2 /></InputAdornment> }} />
      );
    case 'ocr':
      return (
        <FormFileUpload
          label={label || translateUiText('Scan Document (OCR)', t)}
          value={String(value ?? '')}
          onChange={(url) => onChange(field.key, url)}
          folder={`forms/${field.key}`}
          accept="image/*,.pdf,application/pdf"
          required={field.required}
        />
      );
    default:
      return <TextField {...common} />;
  }
}

export default function DynamicFormRenderer({ form, onSuccess, submitLabel }: DynamicFormRendererProps) {
  const theme = useTheme();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);

  const sortedFields = useMemo(
    () => [...form.fields].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    [form.fields]
  );

  const mutation = useMutation({
    mutationFn: () => submitDynamicForm(form.code, values),
    onSuccess: () => {
      setDone(true);
      onSuccess?.(values);
    },
  });

  const setField = (key: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validate = () => {
    const next: Record<string, string> = {};
    for (const field of sortedFields) {
      const raw = values[field.key];
      const empty = raw == null || (typeof raw === 'string' && !raw.trim()) || (Array.isArray(raw) && raw.length === 0);
      if (field.required && empty) {
        next[field.key] = `${field.label || field.key} is required`;
        continue;
      }
      if (field.type === 'email' && !empty) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(raw))) {
          next[field.key] = 'Enter a valid email';
        }
      }
      if (field.type === 'phone' && !empty) {
        const digits = String(raw).replace(/\D/g, '');
        if (digits.length < 8) next[field.key] = 'Enter a valid phone number';
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  if (done) {
    return (
      <Alert severity="success" icon={<CheckCircle />} sx={{ borderRadius: 2 }}>
        {form.name} submitted successfully. No code changes were required — this form is fully metadata-driven.
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); if (validate()) mutation.mutate(); }}>
      <Box sx={{ mb: 2.5, p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
        <Typography variant="subtitle2" fontWeight={700}>{form.name}</Typography>
        <Typography variant="caption" color="text.secondary">{form.entityType} · {form.code}</Typography>
      </Box>
      {Object.keys(errors).length > 0 && (
        <Alert severity="error" sx={{ mb: 2, fontWeight: 700 }}>Please fill in all required fields.</Alert>
      )}
      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 2, fontWeight: 700 }}>
          {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to submit'}
        </Alert>
      )}
      <Grid container spacing={2}>
        {sortedFields.map((field) => (
          <Grid item xs={12} sm={field.type === 'textarea' ? 12 : 6} key={field.key}>
            <FieldRenderer field={field} value={values[field.key]} onChange={setField} error={errors[field.key]} />
          </Grid>
        ))}
      </Grid>
      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={mutation.isPending}
        sx={{ mt: 3, minWidth: 180 }}
      >
        {mutation.isPending ? <CircularProgress size={22} color="inherit" /> : (submitLabel || (form.settings?.submitLabel as string) || 'Submit')}
      </Button>
    </Box>
  );
}
